import React from 'react';
import WelcomeMessageEditor from '../../../../utils/WelcomeMessageEditor';

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

const getEmbedUrl = (url) => {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
};

const FlowSetupStep = ({ formData, update, error, hrAssignees = [] }) => {
  const stepFlags = formData.step_flags || { welcome: true, forms: true, documents: true };
  const welcome = formData.welcome || {};

  const enabledCount = Object.values(stepFlags).filter(Boolean).length;

  const setFlag = (key, val) => {
    if (key === 'welcome') return;
    if (!val && enabledCount <= 1) return;
    update('step_flags', { ...stepFlags, [key]: val });
  };
  const setWelcome = (key, val) => update('welcome', { ...welcome, [key]: val });

  const previewTitle = welcome.title || "Welcome to {{company_name}} — We're thrilled to have you!";
  const previewMsg = stripHtml(welcome.message) || 'This onboarding portal will guide you through everything you need to complete before your first day. Please fill out the forms, review the policies, and reach out if you have any questions.';

  const innerCard = {
    background: '#fff',
    border: '1px solid #e4e9f0',
    borderRadius: 12,
    padding: 22,
  };

  return (
    <div>
      <div className="ob-hint ob-hint--info" style={{ marginBottom: 18 }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <span>
          <strong>Flow setup</strong> — name your flow, assign HR, and optional welcome copy.
        </span>
      </div>

      {/* 2-column: Flow details + Candidate preview */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>

        {/* Flow details */}
        <div style={innerCard}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', marginBottom: 16 }}>Flow details</div>

          <div className="ob-field">
            <label>Flow name <span className="required">*</span></label>
            <input
              type="text"
              placeholder="e.g. Engineering Onboarding 2026"
              value={formData.name}
              onChange={e => update('name', e.target.value)}
              style={{ borderColor: error && !formData.name.trim() ? '#ef4444' : undefined }}
            />
          </div>

          <div className="ob-field">
            <label>Description <span className="optional">(optional)</span></label>
            <textarea
              rows={3}
              placeholder="Brief description of this onboarding flow…"
              value={formData.description}
              onChange={e => update('description', e.target.value)}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="ob-field" style={{ marginBottom: 0 }}>
            <label>HR assignment <span className="required">*</span></label>
            {hrAssignees.length > 0 ? (
              <>
                <select
                  value={formData.hr_assigned_user_id}
                  onChange={e => update('hr_assigned_user_id', e.target.value)}
                >
                  <option value="">— Select HR assignee —</option>
                  {hrAssignees.map(hr => (
                    <option key={hr.id} value={hr.id}>
                      {hr.name}{hr.email ? ` (${hr.email})` : ''}
                    </option>
                  ))}
                </select>
                <div className="ob-field__hint">Tenant user notified when a candidate completes onboarding (not shown to candidates).</div>
              </>
            ) : (
              <div style={{ fontSize: 12.5, color: '#8a9199', padding: '9px 0' }}>Loading HR assignees…</div>
            )}
          </div>
        </div>

        {/* Candidate preview */}
        <div style={innerCard}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', marginBottom: 16 }}>Candidate preview</div>
          <div style={{
            border: '1.5px dashed #d0d5dd',
            borderRadius: 12,
            padding: 20,
            background: '#fafbfc',
            textAlign: 'center',
          }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: '#9aa3b0',
              textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 12,
            }}>
              WHEN WELCOME IS ENABLED
            </div>
            <div style={{
              width: 44, height: 44, borderRadius: '50%', background: '#dbeafe',
              margin: '0 auto 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
            }}>
              🏢
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', marginBottom: 6 }}>
              {previewTitle.length > 52 ? previewTitle.slice(0, 52) + '…' : previewTitle}
            </div>
            <div style={{ fontSize: 11.5, color: '#5b6475', lineHeight: 1.6 }}>
              {previewMsg.length > 120 ? previewMsg.slice(0, 120) + '…' : previewMsg}
            </div>
            {welcome.videoUrl && (() => {
              const embedUrl = getEmbedUrl(welcome.videoUrl);
              return embedUrl ? (
                <div style={{ marginTop: 14, borderRadius: 8, overflow: 'hidden', lineHeight: 0 }}>
                  <iframe
                    src={embedUrl}
                    width="100%"
                    height="160"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Welcome video preview"
                    style={{ borderRadius: 8, display: 'block' }}
                  />
                </div>
              ) : (
                <div style={{
                  marginTop: 14, background: '#fff', border: '1px solid #e4e9f0',
                  borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#5b6475',
                }}>
                  ▶ Video linked
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Welcome content */}
      <div style={{ ...innerCard, marginBottom: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', marginBottom: 16 }}>
          Welcome content{' '}
          <span style={{ color: '#8a9199', fontWeight: 400, fontSize: 12 }}>(optional)</span>
        </div>

        <div className="ob-field">
          <label>Welcome title</label>
          <input
            type="text"
            placeholder="e.g. Welcome to Gappeo — We're thrilled to have you!"
            value={welcome.title || ''}
            onChange={e => setWelcome('title', e.target.value)}
          />
          <div className="ob-field__hint">
            You can use <strong>{'{{candidate_name}}'}</strong> and <strong>{'{{company_name}}'}</strong> as dynamic placeholders.
          </div>
        </div>

        <div className="ob-field">
          <label>Introduction message</label>
          <WelcomeMessageEditor
            value={welcome.message || ''}
            onChange={val => setWelcome('message', val)}
          />
          <div className="ob-field__hint">
            You can use <strong>{'{{candidate_name}}'}</strong> and <strong>{'{{company_name}}'}</strong> as dynamic placeholders.
          </div>
        </div>

        <div className="ob-field" style={{ marginBottom: 0 }}>
          <label>Introductory video URL <span className="optional">(optional)</span></label>
          <input
            type="url"
            placeholder="https://youtube.com/watch?v=…"
            value={welcome.videoUrl || ''}
            onChange={e => setWelcome('videoUrl', e.target.value)}
          />
          <div className="ob-field__hint">Embed a welcome video for the candidate (YouTube or Vimeo).</div>
        </div>
      </div>

      {/* Steps to include */}
      <div style={innerCard}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', marginBottom: 12 }}>Steps to include</div>
        <div className="ob-hint ob-hint--info" style={{ marginBottom: 14 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 10 4 15 9 20" /><path d="M20 4h-7a4 4 0 0 0-4 4v7" />
          </svg>
          Turn sections off to shorten the candidate portal. Review is always included.
        </div>
        <div style={{ display: 'grid', gap: 10, fontSize: 13, color: '#525b66' }}>
          {[
            { key: 'welcome', label: 'Welcome screen', mandatory: true },
            { key: 'forms', label: 'Custom forms' },
            { key: 'documents', label: 'Company documents' },
          ].map(({ key, label, mandatory }) => {
            const isLastEnabled = !mandatory && !!stepFlags[key] && enabledCount === 1;
            const isDisabled = mandatory || isLastEnabled;
            return (
              <label
                key={key}
                style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isLastEnabled ? 0.6 : 1 }}
                title={mandatory ? 'Welcome screen is always required' : isLastEnabled ? 'At least one step must be enabled' : undefined}
              >
                <input
                  type="checkbox"
                  checked={mandatory ? true : !!stepFlags[key]}
                  onChange={e => setFlag(key, e.target.checked)}
                  disabled={isDisabled}
                  style={{ width: 'auto', cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                />
                {label}
                {/* {mandatory && (
                  <span style={{ fontSize: 10.5, fontWeight: 600, color: '#6366f1', background: '#ede9fe', borderRadius: 4, padding: '1px 6px', letterSpacing: '0.3px' }}>
                    Required
                  </span>
                )} */}
              </label>
            );
          })}
        </div>
        {enabledCount === 1 && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#b45309', display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            At least one step must be enabled.
          </div>
        )}
      </div>
    </div>
  );
};

export default FlowSetupStep;
